import {
  type JobApplicant,
  JobApplicantStatus,
  type JobApplicantStatusHistory,
  type UserProfileWithRelations,
} from '@factoryfixinc/ats-interfaces';
import { useMeStore } from '@/core/shared/me/me.store';
import { useFFRestApi } from '@/composables/useApi';
import { useConversationProProfileStore } from './pro-profile.store';
import type { ProUserProfileWithRelations } from './pro-profile';
import type { JobApplicantWithJobTitle } from '@/core/shared/job/job.type';
import ConversationIndexService from '@/core/conversations/conversation-index/conversation-index.service';
import {
  ApplicationBulkActions,
  type ApplicationIndexWithJobApplicantSharedFields,
  type BulkChangeApplicationStatusResult,
} from '@/core/conversations/conversation-index/conversation-index.type';
import ProNotesService from '@/core/shared/pro-notes/pro-notes.service';
import { JobApplicantPersistence } from '@/core/shared/job-applicant/job-applicant.persistence';
import type { UserMessageStatus } from '../message/types/get-user-message-status.type';
import {
  getRejectedsErrors,
  getResolvedDataItems,
  promisePoolAllSettled,
} from '@/utils/promise.util';
import type { CustomJobApplicant } from '@/utils/export-xlsx-utils';
import { ProProfilePersistence } from './pro-profile.persistence';
import { compareApplicationScores } from '../application-score/application-score.util';
import { ErrorService } from '@/core/shared/errors/error.service';

export default class ProProfileService {
  private readonly jobApplicantPersistence = new JobApplicantPersistence();
  private persist = new ProProfilePersistence();
  private conversationProfileStore = useConversationProProfileStore();
  private proNoteService = new ProNotesService();

  public get selectedProJobApplicantStatusHistory(): JobApplicantStatusHistory[] {
    return this.conversationProfileStore.selectedProJobApplicantStatusHistory;
  }

  public static findPrimaryApplication<T extends ApplicationIndexWithJobApplicantSharedFields>(
    applications: T[] | undefined,
  ): T | undefined {
    const conversationIndexJobIds = new ConversationIndexService().getConversationIndexJobIds();
    const filteredJobIdsSet = new Set(conversationIndexJobIds);
    const applicationIndexesFilteredBySelectedJobIds = applications?.filter(({ jobId }) => {
      if (filteredJobIdsSet.size > 0 && jobId) {
        return filteredJobIdsSet.has(jobId);
      }
      return true;
    });
    return applicationIndexesFilteredBySelectedJobIds?.sort((app1, app2) =>
      compareApplicationScores(app1.score, app2.score),
    )?.[0];
  }

  public async getProProfile(
    employerId: number,
    userProfileId: number,
    signal?: AbortSignal,
    options: { preserveSelectionOnError?: boolean; isSelection?: boolean } = {},
  ): Promise<ProUserProfileWithRelations | undefined> {
    // Mark this user-profile id as the latest requested one. Late responses for
    // older requests are dropped by the store's freshness guard.
    const requestId = this.conversationProfileStore.requestPro(userProfileId, employerId, {
      isSelection: options.isSelection,
    });

    const filter = encodeURIComponent(
      JSON.stringify({
        include: [
          { relation: 'pro' },
          {
            relation: 'jobApplicants',
            scope: {
              include: [
                {
                  relation: 'job',
                  scope: {
                    include: [{ relation: 'jobTitle' }],
                  },
                },
              ],
              where: { status: { nin: ['Notified'] } },
            },
          },
          {
            relation: 'proAchievementMaps',
            scope: { include: [{ relation: 'achievement' }] },
          },
        ],
      }),
    );

    const { data: userProfile, error } = await useFFRestApi(
      `/employer/${employerId}/pro/${userProfileId}?filter=${filter}`,
    )
      .get()
      .json<ProUserProfileWithRelations>();

    // A newer request for another pro owns the stores now; this response must
    // not clear, reselect or fetch notes for it.
    const isSuperseded = () =>
      this.conversationProfileStore.profileRequestId !== requestId ||
      (useMeStore().employer?.id !== undefined && useMeStore().employer?.id !== employerId);

    if (error?.value || !userProfile.value) {
      // Return silently for a request the caller has already abandoned.
      // Throwing here would bubble up to `useJobsRouting`'s catch block and
      // fire the "Could not load page data" snackbar — unwanted noise when
      // the user simply navigated away. The caller checks `signal?.aborted`
      // after its own await and bails, so `undefined` is safe.
      // An abandoned flight still owns the pending id until a successor claims
      // it; leaving it set strands the panel on a skeleton nobody will clear.
      if (signal?.aborted) {
        this.conversationProfileStore.settleProfileRequest(requestId);
        return undefined;
      }
      if (!isSuperseded()) {
        // A best-effort refresh of the candidate already on screen must not raise
        // the error pane over a change that has already committed.
        // Before `clearSelectedPro`, which bumps the request generation.
        this.conversationProfileStore.settleProfileRequest(
          requestId,
          options.preserveSelectionOnError ? null : userProfileId,
        );
        if (!options.preserveSelectionOnError) {
          this.conversationProfileStore.clearSelectedPro();
          this.conversationProfileStore.clearSelectedProApplication();
        }
      }
      throw new Error(error.value);
    }

    if (signal?.aborted || isSuperseded()) {
      // A no-op once a successor exists; it only matters when none does.
      this.conversationProfileStore.settleProfileRequest(requestId);
      return userProfile.value;
    }

    this.conversationProfileStore.settleProfileRequest(requestId);
    this.conversationProfileStore.setSelectedPro(userProfile.value);
    const primaryApplication = this.getPrimaryApplication(userProfile.value);
    if (primaryApplication) {
      this.conversationProfileStore.selectJobApplication(primaryApplication.id);
    }

    this.proNoteService.fetchProNotes(userProfile.value.pro.id);
    return userProfile.value;
  }

  public async getProProfileById(
    userProfileId: number,
    employerId: number,
  ): Promise<UserProfileWithRelations<'pro'> | undefined> {
    const proProfile = await this.persist.getByIdWithRelations(userProfileId, employerId, ['pro']);
    return proProfile;
  }

  public async changeApplicationStatus(
    applicationId: JobApplicant['id'],
    newStatus: JobApplicantStatus,
    reason: string = '',
    isSilent: boolean = true,
  ): Promise<void> {
    await this.jobApplicantPersistence.update(
      applicationId,
      { status: newStatus, rejectionReason: reason },
      isSilent,
    );
    const newJobApplicant = await this.jobApplicantPersistence.get(applicationId);
    this.conversationProfileStore.patchApplicationStatus(applicationId, newJobApplicant);
  }

  public async bulkChangeApplicationStatus(
    applicationIds: number[] | undefined,
    action: ApplicationBulkActions,
    isSilent: boolean = true,
    reason: string = '',
  ): Promise<BulkChangeApplicationStatusResult | undefined> {
    if (!applicationIds) return;
    const newStatus = this.findStatusByBulkAction(action);

    const applicationsIds = applicationIds.map((id) => id);

    const poolLimit = 5;
    const result = await promisePoolAllSettled<number, void>(poolLimit, applicationsIds, (id) =>
      this.jobApplicantPersistence.update(
        id,
        { status: newStatus, rejectionReason: reason },
        isSilent,
      ),
    );

    const successCount = getResolvedDataItems(result).length;
    const errorCount = getRejectedsErrors(result).length;

    return { result, successCount, errorCount };
  }

  private findStatusByBulkAction(action: ApplicationBulkActions): JobApplicantStatus {
    switch (action) {
      case ApplicationBulkActions.MOVE_TO_REJECTED:
      case ApplicationBulkActions.MOVE_TO_REJECTED_W_REASON:
        return JobApplicantStatus.REJECTED;
      case ApplicationBulkActions.MOVE_TO_REVIEWED:
        return JobApplicantStatus.REVIEW;
      default:
        return JobApplicantStatus.CLIENT;
    }
  }

  public async getApplicantsByApplicationId(
    applicationId: number,
    applicantIds: number[],
  ): Promise<CustomJobApplicant[] | undefined> {
    //We fetch only the selected applicants from the server
    const filter = `{"where": {"JobApplicant.id": {"inq": [${applicantIds}] } }}`;

    const jobApplicants = await this.jobApplicantPersistence.getApplicantsByJobId(
      applicationId,
      filter,
    );

    return jobApplicants;
  }

  public async getApplicantStatusHistory(): Promise<JobApplicantStatusHistory[]> {
    const applicationId = this.selectedProApplication?.id;

    if (!applicationId) {
      this.conversationProfileStore.selectedProJobApplicantStatusHistory = [];
      return [];
    }

    const statusHistory =
      await this.jobApplicantPersistence.getApplicantsStatusHistory(applicationId);
    this.conversationProfileStore.selectedProJobApplicantStatusHistory = statusHistory;

    return statusHistory;
  }

  // this should select the application according to the rules of precedence.
  // initially this will be a simplified version and later based on score
  private getPrimaryApplication(
    userProfile: ProUserProfileWithRelations,
  ): JobApplicant | undefined {
    if (!userProfile) {
      return;
    }
    const { jobApplicants } = userProfile;

    return ProProfileService.findPrimaryApplication<JobApplicant>(jobApplicants);
  }

  public get selectedProProfile(): ProUserProfileWithRelations | null {
    return this.conversationProfileStore.selectedPro || null;
  }

  public get isLoadingSelectedProProfile(): boolean {
    return this.conversationProfileStore.loadingProfileRequestId !== null;
  }

  public get didSelectedProProfileFail(): boolean {
    const { failedProfileProId, requestedProId } = this.conversationProfileStore;
    return failedProfileProId !== null && failedProfileProId === requestedProId;
  }

  /** Re-issues the profile fetch for the candidate the panel is showing. */
  public async retrySelectedProProfile(): Promise<void> {
    const { requestedProId, requestedEmployerId } = this.conversationProfileStore;
    if (!requestedProId || !requestedEmployerId) return;

    try {
      await this.getProProfile(requestedEmployerId, requestedProId, undefined, {
        isSelection: true,
      });
    } catch (error) {
      // The panel already shows the error state; this must not reach a caller.
      ErrorService.captureException(error);
    }
  }

  public get selectedProApplication(): JobApplicantWithJobTitle | undefined {
    return this.conversationProfileStore.selectedProApplication;
  }

  public get selectedProMessageStatus(): UserMessageStatus | undefined {
    return this.conversationProfileStore.selectedProMessageStatus;
  }

  public get initiatedByOutreach(): boolean | undefined {
    return (
      !!this.selectedProApplication?.initiatedByOutreach &&
      !this.selectedProApplication?.initiatedByCopilot
    );
  }

  public async downloadProfilePdf(applicationId: number): Promise<string> {
    const { data, error } = await useFFRestApi(`/job-applicant/${applicationId}/profile-pdf`)
      .get()
      .text();
    if (error?.value) {
      throw new Error(error.value);
    }
    return data.value || '';
  }
}
